import { LogoutOptions, useAuth0 } from '@auth0/auth0-react';
import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';

type HiqLogoutContextType = {
  logoutFromAllHiqApps: (options?: LogoutOptions) => Promise<void>;
};

export const HiqLogoutContext = createContext<HiqLogoutContextType | null>(
  null
);

type HiqLogoutProviderProps = {
  returnTo?: string;
  pollingIntervalMs?: number;
};

export function HiqLogoutProvider({
  returnTo = window.location.origin,
  pollingIntervalMs = 1000,
  children,
}: PropsWithChildren<HiqLogoutProviderProps>) {
  const HIQ_LOGOUT_COOKIE_NAME = 'hiqLogout';
  const hiqLogoutCookieDomain = window.location.hostname.includes('localhost')
    ? 'localhost'
    : '.harvestiq.com';
  const HIQ_LOGOUT_COOKIE_DURATION_SECONDS = 60; // Duration for which the cookie is valid
  const HIQ_LOGOUT_COOKIE = `${HIQ_LOGOUT_COOKIE_NAME}=true; domain=${hiqLogoutCookieDomain}; path=/; SameSite=Lax;`;

  const { isAuthenticated, isLoading: isLoadingAuth0, logout } = useAuth0();
  const [hiqLogoutCheckIntervalId, setHiqLogoutCheckIntervalId] =
    useState<NodeJS.Timeout | null>(null);

  function checkHiqLogoutStatus() {
    // Check if the hiq logout cookie is set
    const hiqLogoutCookie = document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${HIQ_LOGOUT_COOKIE_NAME}=`));

    // If we have a cookie then another app has logged out
    if (hiqLogoutCookie) {
      console.log('User has logged out from another HIQ app, logging out...');

      // Force local logout, stop polling, and redirect to the configured returnTo URL
      if (hiqLogoutCheckIntervalId) {
        clearInterval(hiqLogoutCheckIntervalId);
      }
      void logout({ logoutParams: { returnTo } });
    }
  }

  // Poll for the hiq logout status from cookie after user is logged in
  useEffect(() => {
    if (!isLoadingAuth0 && isAuthenticated) {
      // Remove the hiqLogout cookie if it exists
      document.cookie = `${HIQ_LOGOUT_COOKIE} max-age=0`;

      console.log('Starting HIQ logout status polling...');
      const intervalId = setInterval(checkHiqLogoutStatus, pollingIntervalMs);
      setHiqLogoutCheckIntervalId(intervalId);

      // Remove the interval when the component unmounts
      return () => clearInterval(intervalId);
    }
    return;
  }, [isLoadingAuth0, isAuthenticated]);

  // Function to log out of all HIQ apps by setting the hiqLogout cookie
  function logoutFromAllHiqApps(options?: LogoutOptions): Promise<void> {
    // Stop polling for logout status from other HIQ apps
    if (hiqLogoutCheckIntervalId) {
      clearInterval(hiqLogoutCheckIntervalId);
    }

    // Set the hiqLogout cookie to trigger logout in other apps
    console.log('Setting hiqLogout cookie');
    document.cookie = `${HIQ_LOGOUT_COOKIE} max-age=${HIQ_LOGOUT_COOKIE_DURATION_SECONDS}`;

    // Call original Auth0 Logout function
    return logout(options);
  }

  const contextValue: HiqLogoutContextType = {
    logoutFromAllHiqApps,
  };

  return (
    <HiqLogoutContext.Provider value={contextValue}>
      {children}
    </HiqLogoutContext.Provider>
  );
}

export function useHiqLogout() {
  const context = useContext(HiqLogoutContext);
  if (!context) {
    throw new Error('useHiqLogout must be used within a HiqLogoutProvider');
  }
  return context;
}
